/**
 * AppHeader — sticky top navigation surfaced across every tenant page.
 *
 * Responsive behaviour:
 *   - Desktop / laptop (≥ 980px): single-row layout, no wrapping.
 *   - Mobile / narrow tablet (< 980px): primary nav collapses into a
 *     burger menu that opens a liquid-glass dropdown panel.
 *
 * Nav items follow permission + role gating (RBAC + plan feature flags):
 *   - Home (scan:run)
 *   - Pipeline (signal:view + pipeline_dashboard)
 *   - Tracker (tracker:view + sector_tracker)
 *   - Webhooks (webhook:view + webhooks)
 *   - Users (user:list)
 *   - Settings (tenant:view_settings)
 *   - Profile  (always)
 *   - Platform (is_platform_root)
 *
 * The burger menu is purely a presentational fold of the same nav list —
 * it does NOT bypass any visibility rules.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFeatures } from '../../context/FeatureContext';
import { tenantBasePath, tenantPath } from '../../lib/tenantPath';
import GlobalSearch from '../GlobalSearch';
import NotificationBell from '../NotificationBell';
import HelpMenu from '../HelpMenu';
import Logo from '../branding/Logo';

const MOBILE_QUERY = '(max-width: 980px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

export default function AppHeader({ active, onNavigate }) {
  const { user, logout, hasPermission } = useAuth();
  const { isEnabled } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [menuOpen, setMenuOpen] = useState(false);
  const burgerRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click + Escape; respect single-source-of-truth for state.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        burgerRef.current && !burgerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        if (burgerRef.current) burgerRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Auto-close when crossing back to desktop width.
  useEffect(() => {
    if (!isMobile && menuOpen) setMenuOpen(false);
  }, [isMobile, menuOpen]);

  const handle = useCallback((destination) => {
    setMenuOpen(false);
    if (!user) return;
    // Iter 38c — preserve the tenant base path (/t/<slug>) for every
    // internal navigation. URL is cosmetic; JWT/user.tenant_id stays
    // the authoritative tenancy boundary.
    const base = tenantBasePath(location.pathname);
    if (destination === 'platform') { navigate('/platform'); return; } // platform console is global, never per-tenant
    if (destination === 'settings') { navigate(tenantPath(base, '/settings')); return; }
    if (destination === 'profile')  { navigate(tenantPath(base, '/profile')); return; }
    if (destination === 'users')    { navigate(tenantPath(base, '/users')); return; }
    // Inner nav (Home / Pipeline / Tracker / Webhooks) — when not on
    // the tenant root, navigate back to it (preserving the tenant
    // prefix) with a ?view= hint that App consumes on mount.
    const tenantRoot = base || '/';
    if (location.pathname !== tenantRoot) {
      const qs = destination && destination !== 'home' ? `?view=${destination}` : '';
      navigate(`${tenantRoot}${qs}`);
      return;
    }
    if (onNavigate) onNavigate(destination);
  }, [navigate, location.pathname, onNavigate, user]);

  if (!user) return null;

  // Single nav-item definition source — desktop row and mobile menu share it.
  const items = [
    { id: 'home',     label: 'Home',     show: hasPermission('scan:run'),                                                            testid: 'nav-home' },
    { id: 'pipeline', label: 'Pipeline', show: hasPermission('signal:view') && isEnabled('pipeline_dashboard'),                       testid: 'nav-pipeline' },
    { id: 'tracker',  label: 'Tracker',  show: hasPermission('tracker:view') && isEnabled('sector_tracker'),                          testid: 'nav-tracker' },
    { id: 'webhooks', label: 'Webhooks', show: hasPermission('webhook:view') && isEnabled('webhooks'),                                testid: 'nav-webhooks' },
    { id: 'users',    label: 'Users',    show: hasPermission('user:list') || !!user.is_platform_root,                                 testid: 'nav-users' },
    { id: 'settings', label: 'Settings', show: hasPermission('tenant:view_settings') || !!user.is_platform_root,                      testid: 'nav-settings' },
    { id: 'profile',  label: 'Profile',  show: true,                                                                                  testid: 'nav-profile' },
    { id: 'platform', label: 'Platform', show: !!user.is_platform_root,                                                               testid: 'nav-platform' },
  ].filter((i) => i.show);

  const NavBtn = ({ id, label, testid }) => (
    <button
      className={`nav-btn ${active === id ? 'nav-btn-active' : ''}`}
      onClick={() => handle(id)}
      data-testid={testid}
    >
      {label}
    </button>
  );

  const onBurgerKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setMenuOpen((v) => !v);
    } else if (e.key === 'ArrowDown' && !menuOpen) {
      e.preventDefault();
      setMenuOpen(true);
    }
  };

  return (
    <header className="app-header" data-testid="app-header">
      <div className="app-header-inner">
        <div className="app-header-brand" onClick={() => handle('home')} role="button" tabIndex={0}>
          <Logo variant="header" imgClassName="app-header-logo" />
          <span className="app-header-version">v3</span>
        </div>

        <GlobalSearch />

        <div className="app-header-user">
          <NotificationBell onAction={(path) => navigate(path)} />
          <HelpMenu />
          <span className="app-header-role" data-testid="current-role-label" title={user.email}>
            {user.role_label}
          </span>
          <button
            className="btn btn-outline btn-sm app-header-logout"
            onClick={logout}
            data-testid="logout-button"
          >
            Log out
          </button>

          {/* Burger toggle — visible only on narrow widths via CSS. */}
          <button
            ref={burgerRef}
            type="button"
            className={`app-header-burger ${menuOpen ? 'is-open' : ''}`}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="app-header-mobile-menu"
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
            onKeyDown={onBurgerKey}
            data-testid="app-header-burger"
          >
            <span className="app-header-burger-line" />
            <span className="app-header-burger-line" />
            <span className="app-header-burger-line" />
          </button>
        </div>
      </div>

      {/* Desktop nav — own row underneath the brand/search/user row */}
      <div className="app-header-nav-row">
        <nav
          className="app-header-nav app-header-nav-desktop"
          data-testid="app-header-nav"
          aria-label="Primary"
        >
          {items.map((it) => (
            <NavBtn key={it.id} id={it.id} label={it.label} testid={it.testid} />
          ))}
        </nav>
      </div>

      {/* Mobile dropdown — RBAC/feature-gate filtered (same `items` list) */}
      {menuOpen && (
        <div
          ref={menuRef}
          id="app-header-mobile-menu"
          className="app-header-mobile-menu qf-glass-panel qf-spring-in"
          role="menu"
          aria-label="Primary navigation"
          data-testid="app-header-mobile-menu"
        >
          {items.map((it) => (
            <button
              key={it.id}
              role="menuitem"
              className={`app-header-mobile-item ${active === it.id ? 'is-active' : ''}`}
              onClick={() => handle(it.id)}
              data-testid={`${it.testid}-mobile`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}
